import { api } from './api';

/**
 * Cookie consent, recorded properly.
 *
 * The banner previously stored only that a choice had been made, which meant
 * "Deny" and "Allow all" were indistinguishable afterwards — anything gated on
 * consent would have run either way. This stores the actual categories, with
 * the timestamp and version they were agreed under, so a later policy change
 * can ask again rather than assume.
 */

const STORAGE_KEY = 'gavel-cookie-consent';
const ANON_KEY = 'gavel-anon-id';

/** Bump when the categories or the wording materially change; consent is re-asked. */
export const CONSENT_VERSION = 2;

export type ConsentCategory = 'necessary' | 'preference' | 'statistics' | 'marketing';

export type ConsentChoices = Record<ConsentCategory, boolean>;

export interface ConsentRecord {
    version: number;
    /** ISO timestamp of the moment the choice was made. */
    decidedAt: string;
    choices: ConsentChoices;
}

/** Nothing but strictly necessary cookies. What Deny stores, and the default. */
export const DENY_ALL: ConsentChoices = {
    necessary: true,
    preference: false,
    statistics: false,
    marketing: false,
};

export const ALLOW_ALL: ConsentChoices = {
    necessary: true,
    preference: true,
    statistics: true,
    marketing: true,
};

export const CATEGORY_LABEL: Record<ConsentCategory, string> = {
    necessary: 'Necessary',
    preference: 'Preference',
    statistics: 'Statistics',
    marketing: 'Marketing',
};

/** Necessary cookies keep the site working and cannot be switched off. */
export const LOCKED: ConsentCategory[] = ['necessary'];

export function readConsent(): ConsentRecord | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as ConsentRecord;
        // A record from an older version is treated as no record at all.
        if (parsed?.version !== CONSENT_VERSION) return null;
        return parsed;
    } catch {
        // Corrupt or unreadable storage means we have no consent, not implied consent.
        return null;
    }
}

export function writeConsent(choices: ConsentChoices): ConsentRecord {
    const record: ConsentRecord = {
        version: CONSENT_VERSION,
        decidedAt: new Date().toISOString(),
        // Necessary is always on; storing it false would misrepresent what happens.
        choices: { ...choices, necessary: true },
    };
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
        // Storage refused (private mode, quota). The banner stays for this visit.
    }
    window.dispatchEvent(new CustomEvent('cg:consent-changed', { detail: record }));
    void recordServerSide(record);
    return record;
}

/** Clears the record so the banner asks again. Used by the privacy trigger. */
export function resetConsent(): void {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* nothing to clear */
    }
    window.dispatchEvent(new CustomEvent('cg:consent-changed', { detail: null }));
}

/**
 * Whether a category may run. Absent consent is a no for everything except the
 * strictly necessary — the safe reading, not the convenient one.
 */
export function hasConsent(category: ConsentCategory): boolean {
    if (category === 'necessary') return true;
    return readConsent()?.choices[category] ?? false;
}

/**
 * A random per-browser id, so a consent record can be tied to the browser that
 * gave it without identifying the person behind it. Regenerated if storage is
 * cleared, which is the correct behaviour — the consent is gone too.
 */
function anonymousId(): string {
    try {
        const existing = window.localStorage.getItem(ANON_KEY);
        if (existing) return existing;
        const fresh = crypto.randomUUID();
        window.localStorage.setItem(ANON_KEY, fresh);
        return fresh;
    } catch {
        // No storage: send a throwaway id so the decision is still logged.
        return crypto.randomUUID();
    }
}

/**
 * Files the decision with the API. Best effort by design — the choice is already
 * stored locally and enforced from there, so a failed request must not block the
 * banner from closing or leave the visitor stuck.
 */
function recordServerSide(record: ConsentRecord): void {
    void api
        .post('/consent', {
            anonymousId: anonymousId(),
            version: record.version,
            choices: record.choices,
        })
        .catch(() => {
            /* The local record stands; the evidence trail simply misses this one. */
        });
}
